import { Checkbox as AntdCheckbox, CheckboxProps } from "antd";
import type { CheckboxRef } from "antd/es/checkbox";
import { forwardRef } from "react";
import "./Checkbox.css";

export type CheckboxSize = "sm" | "md";

export interface DesignSystemCheckboxProps extends CheckboxProps {
  /**
   * The label text to display next to the checkbox
   */
  label?: string;

  /**
   * The size variant for the checkbox
   * @default "md"
   */
  size?: CheckboxSize;
}

/**
 * Checkbox Component
 *
 * A customizable checkbox component built on top of Ant Design's Checkbox
 * with design system integration and consistent styling.
 *
 * Features:
 * - Design system color integration with primary black theme
 * - Multiple size variants for different use cases
 * - Full accessibility support inherited from Ant Design
 * - Consistent spacing and typography from design tokens
 *
 * @example
 * // Basic checkbox with label
 * <Checkbox
 *   label="Accept terms and conditions"
 *   checked={accepted}
 *   onChange={(e) => setAccepted(e.target.checked)}
 * />
 *
 * @example
 * // Small size variant for compact layouts
 * <Checkbox
 *   label="Enable notifications"
 *   size="sm"
 *   checked={notificationsEnabled}
 *   onChange={(e) => setNotificationsEnabled(e.target.checked)}
 * />
 *
 * @example
 * // Disabled state
 * <Checkbox
 *   label="This option is disabled"
 *   disabled={true}
 *   checked={false}
 * />
 */
const Checkbox = forwardRef<CheckboxRef, DesignSystemCheckboxProps>(
  ({ label, children, disabled, size = "md", className, ...props }, ref) => {
    const labelClassName = [
      "indigo-checkbox-label",
      `indigo-checkbox-label-${size}`,
      disabled && "indigo-checkbox-label-disabled",
    ]
      .filter(Boolean)
      .join(" ");

    const checkboxClasses = [
      "indigo-checkbox",
      `indigo-checkbox-${size}`,
      disabled && "indigo-checkbox-disabled",
      className,
    ]
      .filter(Boolean)
      .join(" ");

    // Only render content wrapper if there's actually content
    const hasContent = label || children;

    return (
      <AntdCheckbox
        ref={ref}
        disabled={disabled}
        className={checkboxClasses}
        {...props}
      >
        {hasContent && (
          <span className="indigo-checkbox-content">
            {label && <span className={labelClassName}>{label}</span>}
            {children}
          </span>
        )}
      </AntdCheckbox>
    );
  },
);

Checkbox.displayName = "DesignSystemCheckbox";

export default Checkbox;
